import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, HeartPulse, Lock, Nfc } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function LandingPage() {
  const [tag, setTag] = useState("");
  const navigate = useNavigate();

  return (
    <div>
      <section className="relative border-b border-border overflow-hidden">
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-primary">
            Emergency Medical ID
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none mt-6 max-w-3xl">
            When you can&apos;t speak,
            <br />
            <span className="text-primary">your tag does.</span>
          </h1>
          <p className="mt-8 text-base text-muted-foreground max-w-xl leading-relaxed">
            Tap or scan an NFC ECI-Guard tag and instantly see blood type, allergies, medications and
            emergency contacts — no app, no login, no delay.
          </p>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (tag.trim()) navigate(`/tag/${tag.trim().toUpperCase()}`);
            }}
            className="mt-10 flex flex-col sm:flex-row gap-3 max-w-md"
          >
            <Input
              data-testid="landing-tag-input"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              placeholder="Enter tag ID e.g. DEMO123"
              className="h-12 rounded-none bg-[#141414] border-border focus:ring-2 focus:ring-primary text-white uppercase"
            />
            <Button
              type="submit"
              data-testid="landing-lookup-button"
              className="h-12 rounded-none bg-primary hover:bg-[#FF5C53] tracking-[0.15em] uppercase text-xs font-bold gap-2 px-6"
            >
              Look up <ArrowRight className="w-4 h-4" />
            </Button>
          </form>
        </div>
      </section>

      <section className="border-b border-border bg-black">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 grid gap-12 lg:grid-cols-2 lg:items-center">
          <div className="rise">
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-primary">
              The tag
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-4 leading-none">
              A keychain that speaks
              <br />
              for you.
            </h2>
            <dl className="mt-8 space-y-6">
              {[
                ["Front", "Medical alert symbol responders recognise instantly."],
                ["Back", "\u201cTap phone here for emergency information\u201d, with the NFC antenna built in."],
                ["Built for daily life", "Waterproof, no battery, no app to install."],
              ].map(([t, d]) => (
                <div key={t} className="border-l-2 border-primary pl-5">
                  <dt className="text-xs tracking-[0.2em] uppercase font-bold">{t}</dt>
                  <dd className="mt-2 text-sm text-muted-foreground leading-relaxed max-w-sm">{d}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="flex items-center justify-center">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/067e82b2-e4bf-4984-8bf9-6faf4c6c80e1/images/7b387873d53a31e3fd17fd05b725e73d31f3037b21a3452f685c105d83933006.jpeg"
              alt="ECI-Guard NFC medical alert keychain, front and back"
              className="w-full h-auto object-contain"
              style={{ mixBlendMode: "lighten" }}
              data-testid="keychain-product-image"
            />
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-20 grid gap-px sm:grid-cols-3 bg-border border border-border">
        {[
          { icon: Nfc, t: "Tap the tag", d: "NFC scannable — hold any phone near the tag, no app to install." },
          { icon: Lock, t: "Claim it once", d: "First scan lets you register and lock the profile to your account." },
          { icon: HeartPulse, t: "Responders act", d: "High-contrast layout with tap-to-call emergency contacts." },
        ].map((f, i) => (
          <div key={f.t} className="bg-[#0A0A0A] p-8 rise" style={{ animationDelay: `${i * 90}ms` }}>
            <f.icon className="w-7 h-7 text-primary" strokeWidth={2} />
            <h3 className="font-display text-xl font-black tracking-tight mt-6">{f.t}</h3>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.d}</p>
          </div>
        ))}
      </section>
    </div>
  );
}
